import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheck, LogOut, Menu, X, Sparkles, Activity } from 'lucide-react';
import './ConsoleLayout.css';

interface ConsoleLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
}

const ConsoleLayout: React.FC<ConsoleLayoutProps> = ({ children, currentPage }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isDashboardSubdomain = () => {
    const hostname = window.location.hostname;
    return hostname.startsWith('dashboard.') || hostname === 'dashboard.localhost';
  };

  const getCurrentPage = () => {
    if (currentPage) return currentPage;
    const path = location.pathname;
    if (path.startsWith('/dashboard/')) return path.replace('/dashboard/', '');
    if (path.startsWith('/') && path.length > 1) return path.substring(1);
    return 'moderation';
  };

  const activePage = getCurrentPage();

  const navigationItems = [
    {
      name: 'Moderation Hub',
      description: 'AI review board',
      icon: ShieldCheck,
      page: 'moderation'
    }
  ];

  const handleNavClick = (page: string) => {
    const path = isDashboardSubdomain()
      ? page === 'moderation'
        ? '/'
        : `/${page}`
      : `/dashboard/${page}`;
    navigate(path);
    setSidebarOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    if (isDashboardSubdomain()) {
      window.location.href = '/login';
      return;
    }
    navigate('/');
  };

  return (
    <div className="console-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/logo.svg" alt="Novembr" />
            <div>
              <span>Novembr</span>
              <small>Trust Operations</small>
            </div>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={18} />
          </button>
        </div>

        <div className="sidebar-hero">
          <Sparkles size={16} />
          <p>Enterprise moderation cockpit for dating app safety teams.</p>
        </div>

        <nav className="sidebar-nav">
          {navigationItems.map((item) => (
            <button
              key={item.page}
              onClick={() => handleNavClick(item.page)}
              className={`nav-item ${activePage === item.page ? 'active' : ''}`}
            >
              <item.icon size={18} />
              <div className="nav-copy">
                <span>{item.name}</span>
                <small>{item.description}</small>
              </div>
            </button>
          ))}
        </nav>

        <div className="sidebar-metric">
          <Activity size={14} />
          <span>Live moderation pipeline active</span>
        </div>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user?.email?.charAt(0).toUpperCase() || 'U'}</div>
            <div className="user-details">
              <div className="user-name" title={user?.email ?? undefined}>
                {user?.email ? (user.email.length > 24 ? `${user.email.slice(0, 24)}...` : user.email) : ''}
              </div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleSignOut}>
            <LogOut size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      <main className="console-main">
        {!sidebarOpen && (
          <button className="floating-menu-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={18} />
          </button>
        )}
        <div className="console-content">{children}</div>
      </main>

      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

export default ConsoleLayout;
